import React, { useEffect, useRef, useState } from "react";
// @ts-ignore — react-helmet types not installed
import { Helmet } from "react-helmet";
import { Box } from "@mui/material";
import useTheme from "../../common/hooks/useTheme";
import { Theme } from "../../providers/ThemeProvider";
import { ConsultationProvider, useConsultation } from "./ConsultationContext";
import ConsultationStepper from "./components/stepper/ConsultationStepper";
import SplashPanel from "./components/section1-bio-age/SplashPanel";
import BioAgeSection from "./components/section1-bio-age/BioAgeSection";
import SystemsSection from "./components/section2-systems/SystemsSection";
import ProgrammingSection from "./components/section3-programming/ProgrammingSection";
import BackgroundGlow from "./components/shared/BackgroundGlow";
import ConsultationVideoOverlay from "./components/shared/ConsultationVideoOverlay";
import PageLoader from "../../common/ui/PageLoader";
import ScrollableWrapper from "../platform/components/common/ScrollableWrapper";
import { useElevenLabsWidget } from "../reports/hooks/useElevenLabsWidget";

// Intro video shown full-viewport before the splash. Self-hosted MP4 (Azure
// blob); will become per-affiliate config later. The same
// `ConsultationVideoOverlay` is reused for the outro at the end of the
// consultation — only the URL changes (see ConsultationStepper).
const INTRO_VIDEO_URL =
  "https://mypnoe.blob.core.windows.net/consultation/Intro_Video_Digital_Consult_S3.mp4";

const SectionRouter: React.FC = () => {
  const { stepper } = useConsultation();
  if (stepper.isSplash) return <SplashPanel />;
  switch (stepper.current.section.id) {
    case "bio-age":
      return <BioAgeSection />;
    case "systems":
      return <SystemsSection />;
    case "programming":
      return <ProgrammingSection />;
    default:
      return null;
  }
};

const ConsultationBody: React.FC<{ centerContent: boolean }> = ({
  centerContent,
}) => {
  const theme: Theme = useTheme();
  const { customerId, data, stepper } = useConsultation();

  // Re-key (and re-fade) the content only when the *view* changes — splash vs a
  // section — not on every sub-step (those animate internally via `Reveal`).
  const viewKey = stepper.isSplash ? "splash" : stepper.current.section.id;

  if (data.isLoading) return <PageLoader />;

  if (!customerId) {
    return (
      <Box sx={styles.empty(theme)}>
        <Box component="h2" sx={styles.emptyTitle(theme)}>
          No client selected
        </Box>
        <Box component="p" sx={styles.emptyBody(theme)}>
          Open the consultation from a client&apos;s dashboard, or add{" "}
          <Box component="code">?id=&lt;customerId&gt;</Box> to the URL.
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="main"
      key={viewKey}
      sx={[centerContent ? styles.contentCentered : styles.content, styles.viewFade]}
    >
      <SectionRouter />
    </Box>
  );
};

/**
 * Inner shell. Renders the splash (no stepper) when `isSplash`, otherwise
 * the active section + stepper.
 *
 * Scrolling strategy:
 *   - Sections 1 (`bio-age`) and 3 (`programming`): the entire body scrolls
 *     as a single column, wrapped in `ScrollableWrapper`.
 *   - Section 2 (`systems`): no outer scroll. The MainMetricsBar at the top
 *     is always visible; the sidebar and detail panel each get their own
 *     `ScrollableWrapper` inside `SystemsSection`.
 *
 * The stepper sits below either branch — always pinned to the viewport bottom.
 */
// Map Section 3 sub-step ids to anchor div ids rendered by
// `ProgrammingSection`. When the sub-step changes inside Section 3, scroll
// the matching panel into view.
const SECTION_3_ANCHORS: Record<string, string> = {
  "bio-optimization": "consultation-bio-opt-anchor",
  nutrition: "consultation-nutrition-anchor",
  training: "consultation-training-anchor",
};

const ConsultationShell: React.FC = () => {
  const theme: Theme = useTheme();
  const { stepper } = useConsultation();
  const isSplash = stepper.isSplash;
  const sectionId = stepper.current.section.id;
  const subId = stepper.current.sub.id;
  const isBioAgeSection = sectionId === "bio-age";
  const isSystemsSection = sectionId === "systems";
  const centerContent = isSplash || isBioAgeSection;

  // Scroll behavior on stepper navigation:
  //   - Cross-section change (e.g. Section 2 → Section 3): scroll the wrapper
  //     to top.
  //   - Sub-step change within Section 3: scroll the matching panel anchor
  //     into view (so step 3.2 lands on the Nutrition plan section, etc).
  //   - Sub-step change within Section 1 or Section 2: no scroll — each
  //     sub-step replaces the previous view, so there's nothing to anchor to.
  // Section 2 renders inside `bodyFill` (no wrapper) and has internal
  // scrolling; when active the ref is null and the wrapper scroll is skipped.
  const scrollRef = useRef<HTMLDivElement>(null);
  const prevSectionRef = useRef<string>(sectionId);
  useEffect(() => {
    const sectionChanged = prevSectionRef.current !== sectionId;
    prevSectionRef.current = sectionId;

    if (sectionChanged) {
      if (scrollRef.current) {
        scrollRef.current.scrollTo({ top: 0, behavior: "smooth" });
      }
      return;
    }

    if (sectionId === "programming") {
      const anchorId = SECTION_3_ANCHORS[subId];
      if (!anchorId || !scrollRef.current) return;
      // Wait for the panel to be in the DOM and laid out before measuring.
      // Sub-step "nutrition" / "training" mounts the corresponding panel in
      // the same commit that triggers this effect, so the element exists
      // immediately — but we still defer with rAF so layout has flushed.
      requestAnimationFrame(() => {
        const wrapper = scrollRef.current;
        const el = document.getElementById(anchorId);
        if (!wrapper || !el) return;
        const offset =
          el.getBoundingClientRect().top -
          wrapper.getBoundingClientRect().top +
          wrapper.scrollTop;
        wrapper.scrollTo({ top: offset, behavior: "smooth" });
      });
    }
  }, [sectionId, subId]);

  return (
    <Box sx={styles.foreground}>
      {/* Horizontal-scroll area — wraps the body content (which carries the
          1440 px min-width). The stepper renders OUTSIDE this wrapper so it
          stays viewport-width and remains responsive even when the body
          horizontally scrolls underneath it. */}
      <Box sx={styles.horizScrollArea(theme)}>
        <Box sx={styles.minWidthBody}>
          {isSystemsSection ? (
            <Box sx={styles.bodyFill}>
              <ConsultationBody centerContent={centerContent} />
            </Box>
          ) : (
            <ScrollableWrapper ref={scrollRef} customPadding="0">
              <ConsultationBody centerContent={centerContent} />
            </ScrollableWrapper>
          )}
        </Box>
      </Box>
      {!isSplash && <ConsultationStepper />}
    </Box>
  );
};

/**
 * Inner content — gates the full consultation shell behind the intro video.
 * Lives inside `ConsultationProvider` so data fetching kicks off in parallel
 * with the video (by the time the affiliate finishes / skips the intro, the
 * platform + consultation-prep payloads are typically already in cache).
 *
 * The intro plays only when the user is at the splash, i.e. the URL has no
 * `section` param (`stepper.isSplash === true`). Once the user enters the
 * stepper the URL picks up `?section=...&step=...`, so reloading or deep-
 * linking into Section 2/3 lands the user back where they were without
 * replaying the intro.
 *
 * `introWatched` is local state, initialized once at mount from the URL.
 * Subsequent stepper navigation within the same mount won't replay the
 * video (the state stays `true` once set).
 */
const ConsultationScreenContent: React.FC = () => {
  const theme: Theme = useTheme();
  const { stepper, customerId, data, aiActive, demoMode } = useConsultation();
  // Skip the intro on initial mount if the user landed mid-flow (reload,
  // deep link). Otherwise let the video play on the splash.
  const [introWatched, setIntroWatched] = useState(() => !stepper.isSplash);

  // ElevenLabs consultation agent. The hook is gated on a truthy `agentType`,
  // so passing `undefined` while AI is inactive is a true no-op (no script
  // load, no widget mount). Once the user clicks "AI Guided" on the splash,
  // `aiActive` flips and the widget initializes — at which point the data
  // fetch is also (or about to be) done, satisfying the hook's
  // `selectedClientId + isDataLoaded` guard for non-Cheatsheet agents.
  useElevenLabsWidget(
    "",
    aiActive ? "consultation" : undefined,
    customerId,
    !data.isLoading
  );

  // AI-guided mode: the agent drives the consultation, so block the user's
  // own clicks — otherwise manual navigation would fight the agent's. Only
  // two kinds of clicks pass through:
  //   1. Programmatic clicks — `clickDom` fires `el.click()`, which
  //      dispatches an event with `isTrusted: false`; real user input is
  //      always `isTrusted: true`. This is the discriminator.
  //   2. Clicks inside the ElevenLabs widget (mute, end call, text input) —
  //      shadow-DOM retargeting surfaces those with the <elevenlabs-convai>
  //      host as `target`, so a `closest()` check covers the whole widget.
  // Registered on `document` in the CAPTURE phase so the event is killed
  // before React's root-container delegation (and any native listeners)
  // ever see it. Scrolling and hover are unaffected — only clicks.
  // Keyboard "clicks" (Enter/Space on a focused button) also dispatch
  // trusted click events, so they're blocked by the same listener.
  useEffect(() => {
    if (!aiActive) return;
    const blockUserClicks = (e: MouseEvent) => {
      if (!e.isTrusted) return; // programmatic — clickDom
      const target = e.target as HTMLElement | null;
      if (target?.closest?.("#chat-container, elevenlabs-convai")) return;
      e.preventDefault();
      e.stopPropagation();
    };
    document.addEventListener("click", blockUserClicks, true);
    return () => document.removeEventListener("click", blockUserClicks, true);
  }, [aiActive]);

  if (!introWatched) {
    return (
      <Box sx={styles.root(theme)}>
        <ConsultationVideoOverlay
          videoUrl={INTRO_VIDEO_URL}
          onComplete={() => setIntroWatched(true)}
        />
      </Box>
    );
  }

  return (
    <Box sx={styles.root(theme)}>
      {/* Demo badge — only in public demo mode. Fixed top-left (clear of the
          ElevenLabs widget's bottom-right slot), above all content. */}
      {demoMode && <Box sx={styles.demoBadge(theme)}>DEMO</Box>}
      <BackgroundGlow />
      {/* ElevenLabs widget mount point. The script in `useElevenLabsWidget`
          looks up `#chat-container` by id and `appendChild`s the
          `<elevenlabs-convai>` web component into it. Empty until the user
          picks AI Guided; the widget renders itself in a fixed floating
          position so this div doesn't influence the page layout. */}
      <div id="chat-container" />
      <ConsultationShell />
    </Box>
  );
};

const ConsultationScreen: React.FC = () => {
  return (
    <ConsultationProvider>
      <Helmet>
        <title>PNOĒ — Consultation</title>
      </Helmet>
      <ConsultationScreenContent />
    </ConsultationProvider>
  );
};

export default ConsultationScreen;

// The consultation body isn't responsive — the body layout and figure assume
// a ≥ 1440 px viewport. On narrower screens we let the body scroll horizontally
// rather than letting the inner layout collapse. Mirrors the dashboard pattern
// (WorkspaceShell) where the content has a fixed minWidth and the surrounding
// container provides `overflowX: auto`.
//
// The stepper is intentionally OUTSIDE the horizontal-scroll wrapper, so it
// stays viewport-wide and responsive even when the body scrolls underneath.
const CONSULTATION_MIN_WIDTH_PX = 1440;

const styles = {
  root: (theme: Theme) => ({
    width: "100vw",
    minHeight: "100vh",
    backgroundColor: theme.colors_ds.NEUTRAL_BG_MAIN,
    position: "relative" as const,
    overflow: "hidden",
  }),
  // Public-demo indicator. Low-key pill pinned to the top-left, above content
  // and the stepper, clear of the widget's bottom-right slot.
  demoBadge: (theme: Theme) => ({
    position: "fixed" as const,
    top: "12px",
    left: "12px",
    zIndex: 20,
    padding: "4px 10px",
    borderRadius: "6px",
    backgroundColor: theme.colors_ds.PRIMARY_TEXT_MAIN,
    color: theme.colors_ds.NEUTRAL_GENERAL_WHITE,
    fontFamily: "var(--font-Bold)",
    fontSize: "11px",
    letterSpacing: "0.08em",
    lineHeight: 1.4,
    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.12)",
    pointerEvents: "none" as const,
  }),
  foreground: {
    position: "relative" as const,
    zIndex: 1,
    height: "100vh",
    display: "flex",
    flexDirection: "column" as const,
    minHeight: 0,
  },
  // Horizontal-scroll wrapper for the body content. When viewport < 1440 px
  // a scrollbar appears here; the stepper below stays put. Scrollbar styled
  // to match `ScrollableWrapper` — thin, neutral-border tint, transparent
  // track — so vertical and horizontal scrollbars on the consultation feel
  // consistent. Firefox uses `scrollbarColor` / `scrollbarWidth`; Chrome /
  // Safari go through the `::-webkit-scrollbar*` pseudo-elements (which work
  // here because we're inside MUI's `sx={}`, not a plain inline `style={}`).
  horizScrollArea: (theme: Theme) => ({
    flex: 1,
    minHeight: 0,
    overflowX: "auto" as const,
    overflowY: "hidden" as const,
    display: "flex",
    flexDirection: "column" as const,
    // Firefox
    scrollbarColor: `${theme.colors_ds.NEUTRAL_BORDER_DEFAULT} transparent`,
    scrollbarWidth: "thin" as const,
    // Chrome / Safari — note `height` (not `width`) sets the horizontal
    // scrollbar's thickness.
    "&::-webkit-scrollbar": {
      height: "2px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: theme.colors_ds.NEUTRAL_BORDER_DEFAULT,
      borderRadius: "10px",
    },
    "&::-webkit-scrollbar-track": {
      background: "transparent",
    },
  }),
  // Forces a 1440 px minimum for the body — this is what triggers the scroll
  // on `horizScrollArea` when the viewport is narrower. Acts as the column
  // host for the existing ScrollableWrapper / bodyFill children.
  minWidthBody: {
    minWidth: `${CONSULTATION_MIN_WIDTH_PX}px`,
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as const,
  },
  bodyFill: {
    flex: 1,
    minHeight: 0,
    display: "flex",
    flexDirection: "column" as const,
  },
  content: {
    padding: "16px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    flex: 1,
    minHeight: 0,
    width: "100%",
  },
  contentCentered: {
    // Section 1 layout — top-aligned with generous top padding so the card
    // sits comfortably below the viewport top without requiring vertical
    // centering math.
    padding: "120px 64px 32px",
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
  },
  // Gentle fade between views (splash ↔ section, section ↔ section). The
  // `key` on <main> replays this on each view change.
  viewFade: {
    animation: "consultationViewFade 320ms ease",
    "@keyframes consultationViewFade": {
      from: { opacity: 0, transform: "translateY(6px)" },
      to: { opacity: 1, transform: "translateY(0)" },
    },
  },
  empty: (theme: Theme) => ({
    width: "100%",
    maxWidth: "640px",
    margin: "80px auto",
    textAlign: "center" as const,
    fontFamily: "var(--font-Medium)",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  emptyTitle: (theme: Theme) => ({
    margin: 0,
    marginBottom: "8px",
    fontFamily: "var(--font-Bold)",
    fontSize: "20px",
    color: theme.colors_ds.NEUTRAL_TEXT_DEFAULT,
  }),
  emptyBody: (theme: Theme) => ({
    margin: 0,
    fontFamily: "var(--font-Regular)",
    fontSize: "14px",
    color: theme.colors_ds.NEUTRAL_TEXT_WEAK,
    lineHeight: 1.6,
  }),
};
